export type PhoneCountry = {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
};

/** Häufige Länder für die Online-Buchung (Europa + Nachbarländer). */
export const PHONE_COUNTRIES: PhoneCountry[] = [
  { code: "DE", name: "Deutschland", dialCode: "+49", flag: "🇩🇪" },
  { code: "AT", name: "Österreich", dialCode: "+43", flag: "🇦🇹" },
  { code: "CH", name: "Schweiz", dialCode: "+41", flag: "🇨🇭" },
  { code: "NL", name: "Niederlande", dialCode: "+31", flag: "🇳🇱" },
  { code: "BE", name: "Belgien", dialCode: "+32", flag: "🇧🇪" },
  { code: "LU", name: "Luxemburg", dialCode: "+352", flag: "🇱🇺" },
  { code: "FR", name: "Frankreich", dialCode: "+33", flag: "🇫🇷" },
  { code: "IT", name: "Italien", dialCode: "+39", flag: "🇮🇹" },
  { code: "ES", name: "Spanien", dialCode: "+34", flag: "🇪🇸" },
  { code: "PT", name: "Portugal", dialCode: "+351", flag: "🇵🇹" },
  { code: "GB", name: "Vereinigtes Königreich", dialCode: "+44", flag: "🇬🇧" },
  { code: "IE", name: "Irland", dialCode: "+353", flag: "🇮🇪" },
  { code: "DK", name: "Dänemark", dialCode: "+45", flag: "🇩🇰" },
  { code: "SE", name: "Schweden", dialCode: "+46", flag: "🇸🇪" },
  { code: "NO", name: "Norwegen", dialCode: "+47", flag: "🇳🇴" },
  { code: "FI", name: "Finnland", dialCode: "+358", flag: "🇫🇮" },
  { code: "PL", name: "Polen", dialCode: "+48", flag: "🇵🇱" },
  { code: "CZ", name: "Tschechien", dialCode: "+420", flag: "🇨🇿" },
  { code: "SK", name: "Slowakei", dialCode: "+421", flag: "🇸🇰" },
  { code: "HU", name: "Ungarn", dialCode: "+36", flag: "🇭🇺" },
  { code: "RO", name: "Rumänien", dialCode: "+40", flag: "🇷🇴" },
  { code: "BG", name: "Bulgarien", dialCode: "+359", flag: "🇧🇬" },
  { code: "GR", name: "Griechenland", dialCode: "+30", flag: "🇬🇷" },
  { code: "HR", name: "Kroatien", dialCode: "+385", flag: "🇭🇷" },
  { code: "SI", name: "Slowenien", dialCode: "+386", flag: "🇸🇮" },
  { code: "US", name: "USA", dialCode: "+1", flag: "🇺🇸" },
  { code: "CA", name: "Kanada", dialCode: "+1", flag: "🇨🇦" },
];

const COUNTRY_BY_CODE = new Map(PHONE_COUNTRIES.map((c) => [c.code, c]));
const COUNTRY_BY_DIAL = new Map(
  [...PHONE_COUNTRIES].sort((a, b) => b.dialCode.length - a.dialCode.length).map((c) => [c.dialCode, c]),
);

const LOCALE_COUNTRY: Record<string, string> = {
  de: "DE",
  "de-de": "DE",
  "de-at": "AT",
  "de-ch": "CH",
  en: "GB",
  "en-gb": "GB",
  "en-us": "US",
  fr: "FR",
  it: "IT",
  es: "ES",
  nl: "NL",
  pl: "PL",
};

export function defaultPhoneCountry(): PhoneCountry {
  if (typeof navigator !== "undefined") {
    const lang = navigator.language?.toLowerCase() ?? "";
    const fromLocale = LOCALE_COUNTRY[lang] ?? LOCALE_COUNTRY[lang.split("-")[0] ?? ""];
    if (fromLocale) {
      const match = COUNTRY_BY_CODE.get(fromLocale);
      if (match) return match;
    }
  }
  return COUNTRY_BY_CODE.get("DE")!;
}

export function findPhoneCountryByCode(code: string): PhoneCountry | undefined {
  return COUNTRY_BY_CODE.get(code.toUpperCase());
}

export function detectPhoneCountryFromValue(value: string): PhoneCountry | undefined {
  const trimmed = value.trim();
  if (!trimmed.startsWith("+")) return undefined;
  for (const [dialCode, country] of COUNTRY_BY_DIAL) {
    if (trimmed.startsWith(dialCode)) return country;
  }
  return undefined;
}

export function splitPhoneValue(value: string, fallbackCountry: PhoneCountry): {
  country: PhoneCountry;
  nationalNumber: string;
} {
  const detected = detectPhoneCountryFromValue(value);
  const country = detected ?? fallbackCountry;
  let national = value.trim();
  if (national.startsWith(country.dialCode)) {
    national = national.slice(country.dialCode.length);
  } else if (national.startsWith("+")) {
    national = national.replace(/^\+\d+\s*/, "");
  }
  national = national.replace(/[^\d\s/()-]/g, "").trimStart();
  return { country, nationalNumber: national };
}

export function formatPhoneValue(country: PhoneCountry, nationalNumber: string): string {
  const digits = nationalNumber.replace(/[^\d]/g, "");
  if (!digits) return "";
  const grouped = digits.replace(/(\d{3,4})(?=\d)/g, "$1 ").trim();
  return `${country.dialCode} ${grouped}`.trim();
}
